import { Link } from "react-router-dom";
import { Heart, Crown, MapPin, Ruler, Scale } from "lucide-react";
import { Button } from "./ui/button";
import useAnalytics from "../hooks/useAnalytics";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GirlCard = ({ girl, isFavorite, onToggleFavorite, showFavoriteCount = false }) => {
  const mainImage = girl.images?.[0] || "https://images.unsplash.com/photo-1759090987847-7d2b25beb2f0?w=600";
  const { trackGirlClick, trackButtonClick } = useAnalytics();

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Track favorite on server
    try {
      if (isFavorite) {
        await axios.delete(`${API}/girls/${girl.id}/favorite`);
      } else {
        await axios.post(`${API}/girls/${girl.id}/favorite`);
      }
    } catch (error) {
      // Silent fail - local state is primary
    }
    
    onToggleFavorite(girl.id);
  };

  return (
    <div
      className={`group relative bg-monaco-bg-1 rounded-xl overflow-hidden card-hover border ${
        girl.is_advertised 
          ? "border-monaco-gold gold-glow" 
          : "border-white/5 hover:border-white/10"
      }`}
      data-testid={`girl-card-${girl.slug}`}
    >
      {/* VIP Badge */}
      {girl.is_advertised && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-monaco-gold/20 backdrop-blur-sm border border-monaco-gold/50">
          <Crown className="w-3.5 h-3.5 text-monaco-gold" />
          <span className="text-xs font-semibold text-monaco-gold">VIP</span>
        </div>
      )}

      {/* Featured Badge */}
      {girl.is_featured && !girl.is_advertised && (
        <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-monaco-red/20 backdrop-blur-sm border border-monaco-red/50">
          <span className="text-xs font-semibold text-monaco-red">Featured</span>
        </div>
      )}

      {/* Favorite Button */}
      <button
        onClick={handleFavoriteClick}
        className={`absolute top-3 right-3 z-10 flex items-center gap-1 px-2.5 py-1.5 rounded-full transition-all ${
          isFavorite 
            ? "bg-monaco-red text-white" 
            : "bg-black/50 backdrop-blur-sm text-white/70 hover:text-monaco-red hover:bg-black/70"
        }`}
        data-testid={`favorite-btn-${girl.slug}`}
      >
        <Heart className={`w-4 h-4 ${isFavorite ? "fill-current" : ""}`} />
        {(showFavoriteCount || girl.favorite_count > 0) && (
          <span className="text-xs font-medium">{girl.favorite_count || 0}</span>
        )}
      </button>

      {/* Image */}
      <div className="aspect-[3/4] img-zoom">
        <img
          src={mainImage}
          alt={girl.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-monaco-bg-0 via-transparent to-transparent opacity-80" />
      </div>

      {/* Info */}
      <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-4">
        <h3 className="font-display text-base sm:text-xl text-monaco-text-0 mb-1 sm:mb-2">{girl.name}</h3>
        
        {/* Quick Stats - hidden on very small screens */}
        <div className="hidden sm:flex flex-wrap gap-2 mb-3 text-xs text-monaco-text-1">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {girl.country}
          </span>
          <span className="flex items-center gap-1">
            <Ruler className="w-3 h-3" />
            {girl.height_cm} cm
          </span>
          <span className="flex items-center gap-1">
            <Scale className="w-3 h-3" />
            {girl.weight_kg} kg
          </span>
        </div>
        
        {/* Mobile: Just country */}
        <div className="flex sm:hidden gap-1 mb-2 text-[10px] text-monaco-text-2">
          <MapPin className="w-2.5 h-2.5" />
          <span>{girl.country}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-1 sm:gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <Link to={`/girls/${girl.slug}`} className="flex-1" onClick={() => trackGirlClick(girl.id, girl.name)}>
            <Button 
              variant="outline" 
              className="w-full h-7 sm:h-9 text-xs sm:text-sm border-white/20 text-monaco-text-0 hover:bg-white/10"
              data-testid={`view-profile-${girl.slug}`}
            >
              Profil
            </Button>
          </Link>
          <Link to={`/buchen?girl=${girl.id}`} className="flex-1" onClick={() => trackButtonClick("book-girl")}>
            <Button 
              className="w-full h-7 sm:h-9 text-xs sm:text-sm bg-monaco-red hover:bg-monaco-red-hover text-white btn-shine relative overflow-hidden"
              data-testid={`book-btn-${girl.slug}`}
            >
              Buchen
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GirlCard;
